import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddressFixer from '../components/AddressFixer';
import CorridorMap from '../components/CorridorMap';
import Dialog from '../components/Dialog';
import Pomoc from '../components/Pomoc';
import { useOsvezavanje } from '../osvezavanje';
import Section, { useSections } from '../components/Section';
import Rezervacije from '../components/Rezervacije';
import { CorridorPlace, EInvoice, RezervacijaRadnja, RezervacijePregled, RobaZaKupca, StoreDismissed,
  StorePickupDeclaration, UnmarkedDelivery
} from '../types';
import { useSignedIn } from '../session';
import {
  AddressToFix, CustomerPickupResult, DeliveredStop, GoodsToSend, PostingToDo, SetAsideItem,
  SourcingDelivery, StoreDelivery, StoreSummary
} from '../types';
import { dana, mnozina } from '../tekst';
import { proveriOdgovor } from '../services/greska';
import EInvoicePanel from './EInvoicePanel';
import LocalDeliveryPanel from './LocalDeliveryPanel';
import RobaZaKupce from './RobaZaKupce';
import SourcingPanel from './SourcingPanel';
import { cekaIzvor as cekaIzvorZa, nemaIzvora as nemaIzvoraZa } from './potvrda';

import { API_BASE as BASE } from '../services/apiBase';

/** Prazan odgovor o rezervacijama — dok ne stigne pravi, i kad ne stigne. */
const PRAZNE_REZERVACIJE: RezervacijePregled = {
  magacin: '008', izMagacina: '021', stavkeGreska: null, alarmi: [], stavke: []
};

/** Osvežava se sam, jer poslovođa ovaj ekran drži otvoren uz posao. */
const REFRESH_MS = 120_000;

/** Pravac kako ga poslovođa gleda: naziv, dani i mesta redom vožnje. */
interface CorridorView {
  id: number;
  name: string;
  days: string;
  isActive: boolean;
  places: CorridorPlace[];
}

interface Props {
  code: string;
  onSignOut: () => void;
}

/**
 * Vreme isporuke, sa oznakom kad ga vozač nije sam upisao.
 *
 * Poslovođi je razlika bitna: „vozač štiklirao" znači da je roba predata, a
 * GPS vreme samo da je vozilo bilo tu. Ista boja za oboje bi ga lagala.
 */
function isporuceno(row: DeliveredStop) {
  const sat = row.at
    ? new Date(row.at).toLocaleString('sr-RS', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
      })
    : 'bez vremena';

  return (
    <span className={row.source === 'vozač' ? 'flag' : 'flag flag--warn'}>
      {sat}{row.source === 'gps' ? ' (GPS)' : ''}
    </span>
  );
}

/**
 * Ekran poslovođe maloprodajnog objekta.
 *
 * DVE KARTICE, PO PITANJU KOJE POSTAVLJAJU
 *   „Šta me čeka" — sve što traži njegovu ruku, i ništa drugo.
 *   „Pregled" — šta se dešava sa njegovom prodajom; ovde se ne radi ništa.
 *
 *   Dotle je sve stajalo na jednoj kartici, jedanaest odeljaka jedan pod
 *   drugim, sve otvoreno. Posao i pogled su bili razdvojeni samo vodoravnom
 *   crtom na dvanaestom ekranu skrolovanja — pa se posao tražio, umesto da
 *   stoji na vidiku.
 *
 * REDOSLED POSLA SE NE MENJA
 *   Prvo ono što DRŽI ROBU (adresa, izvor, e-faktura, prenos u magacin), pa ono
 *   što DRŽI KNJIGU (kukičanje, knjiženje). Adrese su prve jer bez njih roba ne
 *   može na turu, a ispraviti ih može samo on — kupac je njegov.
 *
 * ODELJAK SE OTVARA SAM SAMO KAD U NJEMU IMA POSLA
 *   Ono što je čovek sklopio ili otvorio pretiče to pravilo — njegova odluka je
 *   novija od naše pretpostavke. Vidi `useSections`.
 */
export default function StoreApp({ code, onSignOut }: Props) {
  const signedIn = useSignedIn();
  const [addresses, setAddresses] = useState<AddressToFix[]>([]);
  const [deliveries, setDeliveries] = useState<StoreDelivery[]>([]);
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [sourcing, setSourcing] = useState<SourcingDelivery[]>([]);
  const [postings, setPostings] = useState<PostingToDo[]>([]);
  /** Roba koju ovaj objekat drži, a čeka je tuđi kupac. */
  const [toSend, setToSend] = useState<GoodsToSend[]>([]);
  /** Tuđa prodaja koja stoji kod mene i čeka prenos u 012. */
  const [odvajanje, setOdvajanje] = useState<SetAsideItem[]>([]);
  /** Šta je od moje prodaje otišlo kupcu, i šta je meni dovezeno. */
  const [delivered, setDelivered] = useState<{ prodato: DeliveredStop[]; primljeno: DeliveredStop[] }>(
    { prodato: [], primljeno: [] });
  const [corridors, setCorridors] = useState<CorridorView[]>([]);

  /** Šta poslovođa gleda: posao koji ga čeka, ili pogled bez menjanja. */
  const [view, setView] = useState<'posao' | 'razvoz' | 'pregled'>('posao');
  const [openCorridorId, setOpenCorridorId] = useState<number | null>(null);
  const [highlightPlaceId, setHighlightPlaceId] = useState<number | null>(null);

  /**
   * Otpremnice koje ERP vodi kao neisporučene, a traže potez. Nisu SVE takve —
   * spisak od sto redova od kojih devedeset ne traži ništa niko ne čita drugi
   * put.
   */
  const [unmarked, setUnmarked] = useState<UnmarkedDelivery[]>([]);

  /** Prodaja ustanovama, van kase. Traži odluku vozimo li je uopšte. */
  const [eInvoices, setEInvoices] = useState<EInvoice[]>([]);

  /**
   * Rezervacije za kupce (magacin 008): šta stoji na nalogu objekta i alarm kad
   * se nešto od toga proda. Black November, 19.10.2026.
   */
  const [rezervacije, setRezervacije] = useState<RezervacijePregled | null>(null);
  const [rezervacijaZauzeto, setRezervacijaZauzeto] = useState(false);

  /**
   * Roba za kupce koja ide u ovaj objekat — kupac dolazi sam ili ga vozi
   * prodavnica, a roba je bila drugde. Od 21.10.2026. je vozi logistika.
   */
  const [robaZaKupce, setRobaZaKupce] = useState<RobaZaKupca[]>([]);

  /** Red za koji se pita „stvarno preuzeo?" — klik kukiči otpremnicu u ERP-u. */
  const [preuzimaKupac, setPreuzimaKupac] = useState<RobaZaKupca | null>(null);
  const [robaZauzeto, setRobaZauzeto] = useState<string | null>(null);
  const [robaIshod, setRobaIshod] = useState<Record<string, string>>({});

  /** Link iz Viber poruke (`?sekcija=rezervacije`) — skok čeka da podaci stignu. */
  const skokIzLinka = useRef<string | null>(
    (() => {
      try {
        return new URLSearchParams(window.location.search).get('sekcija');
      } catch {
        return null;
      }
    })());

  // Izjave „preuzeo/odneo" i sklonjene otpremnice — da ništa ne nestane bez
  // traga i da se pogrešan klik može povući (analiza scenarija, K24).
  const [izjave, setIzjave] = useState<StorePickupDeclaration[]>([]);
  const [sklonjeno, setSklonjeno] = useState<StoreDismissed[]>([]);
  const [povlaci, setPovlaci] = useState<StorePickupDeclaration | null>(null);

  /** Adresa koju poslovođa upravo ispravlja. Otvara se jedna po jedna. */
  const [openAddressId, setOpenAddressId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Odeljci koji pri poslednjem učitavanju NISU stigli. Dok ih ima, ekran ne
   * sme da kaže „Sve je čisto": prazno tada ne znači da posla nema (analiza
   * scenarija, K14, 16.10.2026).
   */
  const [nepotpuno, setNepotpuno] = useState<string[]>([]);

  // Izjava „kupac odneo sam". Cuva se KOJI red je u toku, da se dugme iskljuci
  // samo na njemu — poslovodja ume da klikne dvaput dok ceka odgovor.
  const [odnosim, setOdnosim] = useState<string | null>(null);

  /**
   * Red za koji se pita „stvarno je odneo?". Klik upisuje prenos u ERP i
   * skida robu sa prevoza, pa ne sme da prođe slučajnim dodirom (analiza 2.0,
   * 14.10.2026 — do tada je to bio jedan klik).
   */
  const [potvrdaOdneo, setPotvrdaOdneo] = useState<SetAsideItem | null>(null);
  const [odneoIshod, setOdneoIshod] = useState<Record<string, string>>({});

  /** Koji su odeljci sklopljeni. Pamti se po objektu, ne po pregledaču. */
  const sekcije = useSections(`objekat.${code}`);

  /** Poslovođa za alarm rezervacije: „jeste taj kupac" ili „nije taj kupac". */
  const radnjaRezervacije = async (id: number, radnja: RezervacijaRadnja) => {
    setRezervacijaZauzeto(true);
    try {
      const putanja = `/api/store/${code}/rezervacije/${id}/${radnja}`;
      const odgovor = await fetch(`${BASE}${putanja}`, { method: 'POST', credentials: 'include' });
      await proveriOdgovor(odgovor, putanja);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRezervacijaZauzeto(false);
    }
  };

  /** Povlači izjavu bez naloga u ERP-u: isporuka se vraća u prevoz (K24). */
  const povuciIzjavu = async () => {
    if (!povlaci) return;
    try {
      const putanja = `/api/store/${code}/odneo-sam/${povlaci.id}/povuci`;
      const odgovor = await fetch(`${BASE}${putanja}`, { method: 'POST', credentials: 'include' });
      await proveriOdgovor(odgovor, putanja);
      setPovlaci(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPovlaci(null);
    }
  };

  const load = useCallback(async () => {
    /**
     * Jedan odeljak koji padne ne sme da obori ceo ekran.
     *
     * Ranije su svih deset poziva išla kroz `Promise.all`, a odgovor se
     * upisivao bez provere. Kad ERP zakašlje, njegovi pozivi vrate 503 sa
     * TEKSTOM u telu — tekst je ispravan JSON, pa je prolazio kroz `json()` i
     * završavao u stanju koje ekran gleda kao spisak. Prvi `.filter` bi tada
     * srušio celu stranicu, a poslovođa bi video prazno.
     *
     * Sad svaki deo pada sam za sebe, i uvek na oblik koji ekran ume da
     * prikaže.
     */
    const posrnuli: string[] = [];
    const statusi: number[] = [];

    // Odeljak koji ne stigne ZADRŽAVA ono što je ranije prikazivao — ne briše se
    // na prazno. Do 16.10.2026. je pad ERP-a ili istekla prijava praznila sve
    // spiskove, a ekran je pisao „Sve je čisto" nad neispravljenim adresama (K14).
    async function ucitaj<T>(putanja: string, prazno: T, ime: string): Promise<{ ok: boolean; data: T }> {
      try {
        const odgovor = await fetch(`${BASE}${putanja}`, { credentials: 'include' });

        if (!odgovor.ok) {
          posrnuli.push(ime);
          statusi.push(odgovor.status);
          return { ok: false, data: prazno };
        }

        const telo = await odgovor.json();

        // Oblik mora da odgovara: spisak ostaje spisak i kad server vrati tekst.
        if (Array.isArray(prazno) !== Array.isArray(telo)) {
          posrnuli.push(ime);
          return { ok: false, data: prazno };
        }

        return { ok: true, data: telo as T };
      } catch {
        posrnuli.push(ime);
        return { ok: false, data: prazno };
      }
    }

    const [toFix, mine, summary, where, toPost, routes, send, otislo, nekukiceno, fakture,
           odvoji, izjaveR, sklonjenoR, rezervacijeR, robaR] =
      await Promise.all([
        ucitaj<AddressToFix[]>(`/api/store/${code}/addresses`, [], 'adrese'),
        ucitaj<StoreDelivery[]>(`/api/store/${code}/deliveries`, [], 'isporuke'),
        ucitaj<StoreSummary[]>('/api/store/summary', [], 'objekti'),
        ucitaj<SourcingDelivery[]>(`/api/store/${code}/sourcing`, [], 'odakle roba ide'),
        ucitaj<PostingToDo[]>(`/api/store/${code}/postings`, [], 'nalozi za knjiženje'),
        ucitaj<CorridorView[]>('/api/corridors', [], 'pravci'),
        ucitaj<GoodsToSend[]>(`/api/store/${code}/dovlacenja`, [], 'pošalji u magacin'),
        ucitaj<{ prodato?: DeliveredStop[]; primljeno?: DeliveredStop[] }>(
          `/api/store/${code}/isporuceno`, {}, 'isporučeno'),
        ucitaj<UnmarkedDelivery[]>(`/api/store/${code}/nije-oznaceno`, [], 'nije označeno'),
        ucitaj<EInvoice[]>(`/api/store/${code}/e-fakture`, [], 'e-fakture'),
        ucitaj<SetAsideItem[]>(`/api/store/${code}/odvajanje`, [], 'odvajanje robe'),
        ucitaj<StorePickupDeclaration[]>(`/api/store/${code}/odneo-sam`, [], 'izjave o preuzimanju'),
        ucitaj<StoreDismissed[]>(`/api/store/${code}/sklonjeno`, [], 'sklonjeno iz prevoza'),
        ucitaj<RezervacijePregled>(`/api/store/${code}/rezervacije`, PRAZNE_REZERVACIJE, 'rezervacije za kupce'),
        ucitaj<RobaZaKupca[]>(`/api/store/${code}/roba-za-kupce`, [], 'roba za kupce')
      ]);

    if (toFix.ok) setAddresses(toFix.data);
    if (mine.ok) setDeliveries(mine.data);
    if (summary.ok) setStores(summary.data);
    if (where.ok) setSourcing(where.data);
    if (toPost.ok) setPostings(toPost.data);
    if (nekukiceno.ok) setUnmarked(nekukiceno.data);
    if (fakture.ok) setEInvoices(fakture.data);
    if (routes.ok) setCorridors(routes.data);
    if (send.ok) setToSend(send.data);
    if (odvoji.ok) setOdvajanje(odvoji.data);
    if (izjaveR.ok) setIzjave(izjaveR.data);
    if (sklonjenoR.ok) setSklonjeno(sklonjenoR.data);
    if (robaR.ok) setRobaZaKupce(robaR.data);
    if (rezervacijeR.ok) {
      // Oblik se ne podrazumeva: spisak ostaje spisak i kad server vrati nešto drugo.
      const d = rezervacijeR.data;
      setRezervacije({
        ...PRAZNE_REZERVACIJE,
        ...d,
        alarmi: Array.isArray(d?.alarmi) ? d.alarmi : [],
        stavke: d?.stavke === null ? null : Array.isArray(d?.stavke) ? d.stavke : []
      });
    }
    if (otislo.ok) {
      setDelivered({
        prodato: Array.isArray(otislo.data?.prodato) ? otislo.data.prodato : [],
        primljeno: Array.isArray(otislo.data?.primljeno) ? otislo.data.primljeno : []
      });
    }

    setNepotpuno(posrnuli);

    // Kaže se KOJI deo nije stigao i ZAŠTO, kad se zna — inače poslovođa gleda
    // prazan odeljak i ne zna je li prazan ili pokvaren.
    setError(posrnuli.length === 0
      ? null
      : statusi.includes(401)
        ? 'Prijava više ne važi (istekla je ili je nalog promenjen). Osveži stranicu i '
          + 'prijavi se ponovo — dok to ne uradiš, ništa se ne učitava.'
        : statusi.includes(503)
          ? `ERP trenutno ne odgovara — ne učitava se: ${posrnuli.join(', ')}. Za te delove na `
            + 'ekranu stoji ono što je poslednji put stiglo; osvežava se samo za dva minuta.'
          : `Ne učitava se: ${posrnuli.join(', ')}. Za te delove na ekranu stoji ono što je `
            + 'poslednji put stiglo; osvežava se samo za dva minuta.');
  }, [code]);

  /**
   * „Kupac odneo sam" za jedan red iz spiska tuđe prodaje.
   *
   * Dve stvari slede iz ovoga: stavka izlazi iz prevoza, i prenos iz magacina
   * rezervacija na objekat koji je prodao se upisuje NEPROKNJIŽEN. Ni jedno ni
   * drugo nije knjiženje — nalog knjiži čovek.
   *
   * Ishod ostaje na ekranu i kad je odbijen: kad je stavka na turi, poslovođa
   * mora da PROČITA zašto, a ne da klik tiho ne uradi ništa.
   */
  const odneoSam = useCallback(
    async (row: SetAsideItem) => {
      const kljuc = `${row.documentKey}-${row.articleCode}`;

      setOdnosim(kljuc);
      setError(null);

      try {
        const odgovor = await fetch(`${BASE}/api/store/${code}/odneo-sam`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentKey: row.documentKey,
            articleCode: row.articleCode,
            articleName: row.articleName,
            quantity: row.quantity,
            soldByCode: row.sellerCode
          })
        });

        await proveriOdgovor(odgovor, `/api/store/${code}/odneo-sam`);

        const ishod: CustomerPickupResult = await odgovor.json();

        setOdneoIshod((prethodno) => ({ ...prethodno, [kljuc]: ishod.poruka }));

        // Kad je zapisano, red ispada sa spiska — ali tek posle ponovnog
        // ucitavanja, da se stanje ne razilazi sa bazom.
        if (ishod.zapisano) {
          await load();
        }
      } catch (greska) {
        setOdneoIshod((prethodno) => ({
          ...prethodno,
          [kljuc]: greska instanceof Error ? greska.message : 'Nije uspelo.'
        }));
      } finally {
        setOdnosim(null);
      }
    },
    [code, load]
  );


  /**
   * „Kupac preuzeo" za robu koja je stigla u objekat. Zapisuje se i odmah kukiči u
   * ERP-u; ishod ostaje uz red i kad je odbijen — poslovođa mora da pročita zašto.
   */
  const kupacPreuzeo = useCallback(
    async (red: RobaZaKupca) => {
      setRobaZauzeto(red.documentKey);
      setPreuzimaKupac(null);

      try {
        const putanja = `/api/store/${code}/roba-za-kupce/${encodeURIComponent(red.documentKey)}/preuzeo`;
        const odgovor = await fetch(`${BASE}${putanja}`, { method: 'POST', credentials: 'include' });

        await proveriOdgovor(odgovor, putanja);

        const ishod: { poruka: string } = await odgovor.json();
        setRobaIshod((prethodno) => ({ ...prethodno, [red.documentKey]: ishod.poruka }));
        await load();
      } catch (greska) {
        setRobaIshod((prethodno) => ({
          ...prethodno,
          [red.documentKey]: greska instanceof Error ? greska.message : 'Nije uspelo.'
        }));
      } finally {
        setRobaZauzeto(null);
      }
    },
    [code, load]
  );

  useOsvezavanje(load, REFRESH_MS);

  const me = stores.find((s) => s.code === code);
  const openCorridor = corridors.find((c) => c.id === openCorridorId) ?? null;
  // PRETRAGA na „Pregledu" — kupac zove i pita „gde mi je aparat" (K24).
  const [trazi, setTrazi] = useState('');
  const odgovara = (...polja: (string | null | undefined)[]) => {
    const t = trazi.trim().toLowerCase();
    return t === '' || polja.some((p) => (p ?? '').toLowerCase().includes(t));
  };

  const waiting = deliveries.filter((d) => !d.isOnTour)
    .filter((d) => odgovara(d.customer, d.address, d.city, d.documentKey));
  const onTour = deliveries.filter((d) => d.isOnTour)
    .filter((d) => odgovara(d.customer, d.address, d.city, d.documentKey));

  /**
   * Isporuke kod kojih JOŠ nije rečeno odakle roba ide.
   *
   * Odeljak prikazuje i one o kojima je već odlučeno — da se odluka vidi i po
   * potrebi promeni — ali brojka sme da broji samo posao koji stvarno čeka.
   * Inače poslovođi piše „3 isporuke čekaju" nad ekranom na kom je sve
   * potvrđeno.
   */
  const cekaIzvor = sourcing.filter(cekaIzvorZa);

  /**
   * Isporuke kod kojih robe NEMA NIGDE, a niko još nije rekao da stiže.
   *
   * Broje se odvojeno jer su drugi posao: mesto se ne bira, nego se javlja da
   * je naručeno. Dok su se brojale kroz `cekaIzvor`, nisu se brojale uopšte —
   * i odeljak je nad njima pisao „sve potvrđeno".
   */
  const bezIzvora = sourcing.filter(nemaIzvoraZa);

  /** E-fakture kod kojih još nije rečeno vozimo li ih. */
  const cekaOdluku = eInvoices.filter((i) => i.needsDecision);

  /**
   * Sažetak posla, u redosledu u kom odeljci stoje ispod.
   *
   * `sad` je ono što drži robu — dok stoji, kupac ne dobija isporuku. Ostalo
   * drži knjigu: bitno je, ali roba se u međuvremenu vozi.
   */
  /**
   * Šta od tuđe prodaje kod mene još nema prenos u 012.
   *
   * Gotovi redovi ostaju na ekranu — po njima se vidi da je posao odrađen i
   * koji je nalog — ali ne ulaze u brojku posla koji čeka.
   */
  const cekaPrenos = odvajanje.filter((r) => !r.transferDone);

  const otvoreneRezervacije = (rezervacije?.alarmi ?? []).filter((a) => !a.zatvoren);

  /** Roba za kupca koja je stigla, a kupac je još nije preuzeo — čeka poziv. */
  const cekaKupca = robaZaKupce.filter((r) => r.mozePreuzeo);

  // Link iz poruke vodi na odeljak — kad su podaci stigli i odeljak postoji.
  useEffect(() => {
    if (skokIzLinka.current === 'rezervacije' && rezervacije) {
      skokIzLinka.current = null;
      sekcije.skoci('rezervacije');
    }
  }, [rezervacije, sekcije]);

  // Isto za poruku „roba za kupca je stigla" (`?sekcija=roba-za-kupce`).
  useEffect(() => {
    if (skokIzLinka.current === 'roba-za-kupce' && robaZaKupce.length > 0) {
      skokIzLinka.current = null;
      sekcije.skoci('roba-za-kupce');
    }
  }, [robaZaKupce, sekcije]);

  const poslovi = [
    {
      id: 'rezervacije',
      broj: otvoreneRezervacije.filter((a) => !a.kupacPotvrdjen).length,
      tekst: `${mnozina(otvoreneRezervacije.filter((a) => !a.kupacPotvrdjen).length,
        'prodata rezervacija čeka', 'prodate rezervacije čekaju', 'prodatih rezervacija čeka')}`
             + ' tvoju proveru',
      sad: true
    },
    {
      id: 'roba-za-kupce',
      broj: cekaKupca.length,
      tekst: `${mnozina(cekaKupca.length, 'kupac čeka', 'kupca čekaju', 'kupaca čeka')}`
             + ' poziv — roba je stigla',
      sad: true
    },
    {
      id: 'adrese',
      broj: addresses.length,
      tekst: `${mnozina(addresses.length, 'adresa', 'adrese', 'adresa')} za ispravku`,
      sad: true
    },
    {
      id: 'izvor',
      broj: cekaIzvor.length,
      tekst: `${mnozina(cekaIzvor.length, 'isporuka', 'isporuke', 'isporuka')}`
             + ' čeka odgovor odakle roba ide',
      sad: true
    },
    {
      id: 'izvor',
      broj: bezIzvora.length,
      tekst: `${mnozina(bezIzvora.length, 'isporuka', 'isporuke', 'isporuka')}`
             + ' nema na stanju nigde',
      sad: true
    },
    {
      id: 'e-fakture',
      broj: cekaOdluku.length,
      tekst: `${mnozina(cekaOdluku.length, 'e-faktura', 'e-fakture', 'e-faktura')}`
             + ' čeka odgovor vozimo li',
      sad: true
    },
    {
      id: 'odvajanje',
      broj: cekaPrenos.length,
      tekst: `${mnozina(cekaPrenos.length, 'komad', 'komada', 'komada')}`
             + ' tuđe prodaje čeka prenos u 012',
      sad: true
    },
    {
      id: 'u-magacin',
      broj: toSend.length,
      tekst: `${mnozina(toSend.length, 'otpremnica', 'otpremnice', 'otpremnica')}`
             + ' čeka prenos u magacin',
      sad: true
    },
    {
      id: 'neoznaceno',
      broj: unmarked.length,
      tekst: `${mnozina(unmarked.length, 'otpremnica', 'otpremnice', 'otpremnica')}`
             + ' za kukičanje u ERP-u',
      sad: false
    },
    {
      id: 'knjizenje',
      broj: postings.length,
      tekst: `${mnozina(postings.length, 'nalog', 'naloga', 'naloga')} za knjiženje u ERP-u`,
      sad: false
    }
  ].filter((p) => p.broj > 0);

  const zaMene = poslovi.reduce((zbir, p) => zbir + p.broj, 0);

  /**
   * Veze jednog odeljka sa pamćenjem stanja.
   *
   * `podrazumevano` je otvoren AKO U NJEMU IMA POSLA. Čovekov izbor pretiče.
   */
  function veze(id: string, podrazumevano: boolean) {
    return {
      id,
      open: sekcije.otvoren(id, podrazumevano),
      onToggle: () => sekcije.prebaci(id, podrazumevano)
    };
  }

  return (
    <div className="app">
      <header className="app__header">
        <div>
          <h1>{me?.name ?? `Objekat ${code}`}</h1>
          <p className="muted">Šta čeka tvoju ruku, i šta se dešava sa tvojom prodajom.</p>
        </div>
        <div className="app__who">
          <span className="muted">{signedIn.displayName}</span>
          <button type="button" className="link" onClick={onSignOut}>Odjavi se</button>
        </div>
      </header>

      {error && <div className="health health--bad">{error}</div>}

      <nav className="tabs">
        <button
          type="button"
          className={view === 'posao' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('posao')}
        >
          Šta me čeka
          {/* Brojka stoji i kad je poslovođa na drugoj kartici — inače ne bi
              znao da je u međuvremenu stigao posao. */}
          {zaMene > 0 && <span className="count count--ceka">{zaMene}</span>}
        </button>
        {/* Razvoz koji objekat vozi SAM. Odvojena kartica jer je drugi posao:
            roba je već tu, izvora nema, i vozi je radnik prodavnice. */}
        <button
          type="button"
          className={view === 'razvoz' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('razvoz')}
        >
          Moj razvoz
        </button>
        <button
          type="button"
          className={view === 'pregled' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('pregled')}
        >
          Pregled
        </button>
      </nav>

      <Pomoc ekran="objekat" />

      {view === 'posao' && (<>

      {/* Šta traži RUKU, jednim pogledom. Brojka VODI na svoj odeljak: dok nije
          vodila nikuda, poslovođa je čitao „3 adrese za ispravku" pa ih tražio
          po ekranu. */}
      <div className="todo">
        {zaMene === 0 && nepotpuno.length === 0 ? (
          <p className="todo__clear">
            Nema ništa što čeka tebe. Sve je čisto — na kartici „Pregled" vidiš
            šta se dešava sa robom.
          </p>
        ) : zaMene === 0 ? (
          // Ne sme se reći „Sve je čisto" dok deo ekrana nije stigao (K14).
          <p className="todo__clear">
            Ne zna se da li nešto čeka — ne učitava se: {nepotpuno.join(', ')}.
            Pogledaj poruku iznad.
          </p>
        ) : (
          <ul className="todo__list">
            {poslovi.map((posao) => (
              <li
                // Više poslova vodi u ISTI odeljak (isto sidro), pa samo sidro nije
                // jedinstven ključ — React je mešao redove (analiza 2.0, T10).
                key={`${posao.id}|${posao.tekst}`}
                className={posao.sad ? 'todo__item todo__item--now' : 'todo__item'}
              >
                <button
                  type="button"
                  className="todo__go"
                  onClick={() => sekcije.skoci(posao.id)}
                >
                  <strong>{posao.broj}</strong>
                  {posao.tekst}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Na vrhu: kupac je kupio odvojenu robu i čeka se provera — Black November. */}
      {(otvoreneRezervacije.length > 0 || (rezervacije?.stavke?.length ?? 0) > 0) && (
        <Section
          {...veze('rezervacije', otvoreneRezervacije.length > 0)}
          title="Rezervacije za kupce"
          aside={otvoreneRezervacije.length > 0 ? String(otvoreneRezervacije.length) : undefined}
        >
          <Rezervacije
            pregled={rezervacije}
            zaObjekat
            onRadnja={(id, radnja) => void radnjaRezervacije(id, radnja)}
            zauzeto={rezervacijaZauzeto}
          />
        </Section>
      )}

      {/* Roba za kupce koja ide u ovaj objekat — kad stigne, kupac čeka poziv. */}
      {robaZaKupce.length > 0 && (
        <Section
          {...veze('roba-za-kupce', cekaKupca.length > 0)}
          title="Roba za kupce"
          aside={String(robaZaKupce.length)}
          note={<>
            Kupac dolazi sam (ili ga voziš ti), a roba je bila drugde — dovozi je
            logistika. Kad stigne, javi kupcu; kad je odnese, klikni „Kupac preuzeo" i
            otpremnica se sama kukiči u ERP-u.
          </>}
        >
          <RobaZaKupce
            roba={robaZaKupce}
            onPreuzeo={setPreuzimaKupac}
            zauzeto={robaZauzeto}
            ishod={robaIshod}
          />
        </Section>
      )}

      {preuzimaKupac && (
        <Dialog
          title="Kupac je preuzeo robu?"
          description={
            <>
              <strong>{preuzimaKupac.kupac || '(bez imena)'}</strong> · {preuzimaKupac.documentKey}
              <br /><br />
              Zapisuje se da je kupac odneo svu robu sa otpremnice, i otpremnica se odmah u
              ERP-u označava kao isporučena. Klikni tek kad kupac stvarno odnese robu.
            </>
          }
          confirmLabel="Da, preuzeo je"
          busy={robaZauzeto === preuzimaKupac.documentKey}
          onCancel={() => setPreuzimaKupac(null)}
          onConfirm={() => void kupacPreuzeo(preuzimaKupac)}
        />
      )}

      {addresses.length > 0 && (
        <Section
          {...veze('adrese', true)}
          title="Adrese za ispravku"
          count={addresses.length}
          tone="todo"
          note={<>
            Ove adrese se ne mogu naći na mapi, pa roba ne može na turu. Otvori
            adresu i postavi tačku dok ti je kupac na vezi — tada saznaješ i
            sprat i lift. Tekst adrese ispravi u ERP-u kad stigneš.
          </>}
        >
          <ul className="tofix">
            {addresses.map((address) => (
              <li key={address.id}>
                <button
                  type="button"
                  className="tofix__open"
                  onClick={() =>
                    setOpenAddressId(openAddressId === address.id ? null : address.id)}
                >
                  <span className="tofix__address">
                    {address.rawAddress || '(bez ulice)'}, {address.rawCity || '(bez mesta)'}
                    <span className="muted"> · {address.erpDocumentKey}</span>
                  </span>
                  <span className="tofix__chevron">
                    {openAddressId === address.id ? '▾' : '▸'}
                  </span>
                </button>

                <p className="tofix__what">
                  {address.unknownPlace
                    ? 'Mesto nije u šifarniku pošta — proveri kako je napisano.'
                    : address.needsHouseNumber
                      ? 'Ulica je nađena, kućni broj nije. Fali ili nije tačan.'
                      : address.note}
                </p>

                {address.phones.length > 0 ? (
                  <p className="tofix__phones">
                    {address.phones.map((number) => (
                      <a key={number} href={`tel:${number}`}>{number}</a>
                    ))}
                  </p>
                ) : (
                  <p className="tofix__nophone">Nema ni telefon — adresa se ne može proveriti.</p>
                )}

                {/* Isti alat koji ima operater u magacinu. Poslovođa ga koristi
                    iz drugog razloga: dok razgovara sa kupcem, a ne u nizu. */}
                {openAddressId === address.id && (
                  <div className="tofix__fix">
                    <AddressFixer
                      address={address}
                      onDone={(id) => {
                        setAddresses((current) => current.filter((x) => x.id !== id));
                        setOpenAddressId(null);
                      }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {sourcing.length > 0 && (
        <Section
          {...veze('izvor', cekaIzvor.length > 0 || bezIzvora.length > 0)}
          title="Odakle roba ide"
          count={cekaIzvor.length + bezIzvora.length}
          tone="todo"
          aside={cekaIzvor.length + bezIzvora.length === 0 ? 'sve potvrđeno' : undefined}
        >
          <SourcingPanel
            code={code}
            deliveries={sourcing}
            onSaved={() => void load()}
          />
        </Section>
      )}

      {eInvoices.length > 0 && (
        <Section
          {...veze('e-fakture', cekaOdluku.length > 0)}
          title="E-fakture — vozimo li?"
          count={cekaOdluku.length}
          tone="todo"
          aside={cekaOdluku.length === 0 ? 'sve odlučeno' : undefined}
        >
          <EInvoicePanel code={code} invoices={eInvoices} onSaved={() => void load()} />
        </Section>
      )}

      {/* JEDINI ODELJAK KOJI NIJE MOJA PRODAJA.
          Sve ostalo na ovom ekranu odgovara na „šta sam prodao"; ovde je
          pitanje „šta držim". Postoji zato što je do 15.09.2026. ta molba
          živela samo u mejlu — a mejl ponegde ne stigne, pa posao nestane bez
          traga i otkrije se tek kad vozač dođe po robu koje nema. */}
      {odvajanje.length > 0 && (
        <Section
          {...veze('odvajanje', cekaPrenos.length > 0)}
          title="Odvoji za tuđeg kupca"
          count={cekaPrenos.length}
          tone="todo"
          note={<>
            Ovu robu si prodao ne ti, nego drugi objekat — a ona stoji na tvom
            lageru <strong>kao slobodna</strong> i možeš je prodati drugom kupcu
            pre nego što vozilo dođe. Prenos u magacin <strong>012</strong> je
            sklanja iz prodaje, a fizički ostaje kod tebe.
            <br /><br />
            Isto ovo ti stiže i mejlom. Ovde stoji dok se ne uradi, da se ne
            izgubi ako poruka ne stigne.
          </>}
        >
          <ul className="storelist">
            {odvajanje.map((row) => (
              <li key={`${row.documentKey}-${row.articleCode}`}>
                <span className="row__name">
                  {row.transferDone && <span className="flag flag--ok">preneseno</span>}
                  {' '}{row.articleName} × {row.quantity}
                </span>
                <span className="row__side">
                  {row.transferDone ? (
                    <span className="muted">{row.nalog ? `nalog ${row.nalog}` : 'nalog nađen'}</span>
                  ) : (
                    <span className={row.waitingDays > 2 ? 'flag flag--warn' : 'flag'}>
                      {row.waitingDays === 0 ? 'danas' : `${row.waitingDays} d`}
                    </span>
                  )}
                </span>
                <span className="row__meta">
                  prodao {row.sellerName} · kupac {row.customerName}
                  {row.city ? `, ${row.city}` : ''} · otpremnica {row.documentKey}
                </span>
                {row.restFrom.length > 0 && (
                  <span className="row__meta">
                    ostalo sa te otpremnice stiže iz: {row.restFrom.join(', ')}
                  </span>
                )}
                {/* Kupac ume da dođe i uzme robu tu, kod tebe, umesto da čeka
                    turu. ERP to ima kao šifru 252, ali se ona bira pri
                    zaključenju računa i posle se ne menja — pa odluka koja
                    padne kasnije nema gde da se upiše. Bez ovoga roba ostaje u
                    magacinu 012 i minus kod prodavca stoji otvoren. */}
                <span className="row__meta">
                  <button
                    type="button"
                    disabled={odnosim !== null}
                    onClick={() => setPotvrdaOdneo(row)}
                  >
                    {odnosim === `${row.documentKey}-${row.articleCode}`
                      ? 'Upisujem…'
                      : 'Kupac odneo sam'}
                  </button>
                </span>

                {/* Ishod ostaje na ekranu i kad je odbijen — kad je stavka na
                    turi, poslovođa mora da pročita zašto. */}
                {odneoIshod[`${row.documentKey}-${row.articleCode}`] && (
                  <span className="row__meta row__meta--warn">
                    {odneoIshod[`${row.documentKey}-${row.articleCode}`]}
                  </span>
                )}

                {/* Sumnja, ne nalaz: kroz 002 ide i sva redovna dopuna, pa se
                    ovde ništa ne tvrdi — kaže se šta je nađeno. */}
                {row.misdirectedTo && (
                  <span className="row__meta row__meta--warn">
                    Možda je prenos otišao u {row.misdirectedTo}
                    {row.misdirectedNalog ? ` (nalog ${row.misdirectedNalog})` : ''} umesto u 012.
                    Pogledaj taj nalog: ako jeste ovo, storniraj ga i napravi ponovo u 012.
                  </span>
                )}
              </li>
            ))}
          </ul>

          {potvrdaOdneo && (
            <Dialog
              title="Kupac je robu odneo sam?"
              description={
                <>
                  <strong>{potvrdaOdneo.articleName} × {potvrdaOdneo.quantity}</strong>
                  {' '}— kupac {potvrdaOdneo.customerName}, otpremnica {potvrdaOdneo.documentKey}.
                  <br /><br />
                  Roba izlazi iz prevoza, a u ERP se upisuje prenos iz 012 u objekat
                  koji je prodao ({potvrdaOdneo.sellerName}). Potvrdi samo ako je
                  kupac stvarno odneo robu.
                </>
              }
              confirmLabel="Da, odneo je"
              busy={odnosim !== null}
              onCancel={() => setPotvrdaOdneo(null)}
              onConfirm={() => {
                const red = potvrdaOdneo;
                setPotvrdaOdneo(null);
                void odneoSam(red);
              }}
            />
          )}
        </Section>
      )}

      {toSend.length > 0 && (
        <Section
          {...veze('u-magacin', true)}
          title="Pošalji u magacin"
          count={toSend.length}
          tone="todo"
          note={<>
            Ovu robu čeka kupac koji nije na tvom pravcu, pa dok ne dođe u
            magacin nijedna tura ne može do njega. Napravi interni prenos u
            magacin, kao i svaki drugi — tura ga posle nosi.
          </>}
        >
          <ul className="storelist">
            {toSend.map((row) => (
              <li key={row.documentKey}>
                <span className="row__name">
                  {row.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                </span>
                <span className="row__side">
                  <span className={row.waitingDays > 2 ? 'flag flag--warn' : 'flag'}>
                    {row.waitingDays === 0 ? 'danas' : `${row.waitingDays} d`}
                  </span>
                </span>
                <span className="row__meta">
                  kupac u mestu {row.city} · otpremnica {row.documentKey}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(unmarked.length > 0 || postings.length > 0) && (
        <h3 className="storeapp__divider">
          Ovo se radi u ERP-u — aplikacija samo pokazuje šta stoji
        </h3>
      )}

      {unmarked.length > 0 && (
        <Section
          {...veze('neoznaceno', true)}
          title="Nije označeno kao isporučeno"
          count={unmarked.length}
          tone="todo"
          note={<>
            ERP ove otpremnice i dalje vodi kao neisporučene. Dok kolona stoji na
            nuli, saldo objekta ne zatvara krug.{' '}
            <strong>Kukičaj u ERP-u, kao i do sada</strong>; ovde se samo vidi
            šta stoji.
          </>}
        >
          <ul className="storelist">
            {unmarked.map((row) => (
              <li key={row.documentKey}>
                <span className="row__name">
                  {row.customerName || 'bez imena kupca'}, {row.city}
                </span>

                <span className="row__side">
                  <span className={row.miSmoIsporucili ? 'flag flag--bad' : 'flag flag--warn'}>
                    {row.miSmoIsporucili ? 'mi smo isporučili' : `stoji ${row.waitingDays} d`}
                  </span>
                </span>

                <span className="row__meta">
                  otpremnica {row.documentKey}
                  {row.miSmoIsporucili && row.deliveredAt && (
                    <>
                      {' · '}isporučeno {new Date(row.deliveredAt).toLocaleDateString('sr-RS')}
                      {row.tourName && `, turom „${row.tourName}"`}
                      {' — kukičaj u ERP-u'}
                    </>
                  )}
                  {!row.miSmoIsporucili
                    && ' · kod nas nema traga da je vožena — proveri šta se desilo'}
                  {row.notes && ` · ${row.notes}`}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {postings.length > 0 && (
        <Section
          {...veze('knjizenje', true)}
          title="Nalozi za knjiženje"
          count={postings.length}
          tone="todo"
          note={<>
            Ovi prenosi su upisani u ERP — nalog postoji i roba je prebačena, ali
            <strong> nije proknjižen</strong>. Knjiženje uradi u ERP-u; aplikacija
            to ne dira, jer taj klik ulazi u glavnu i pomoćne knjige. Kad
            proknjižiš, nalog sam nestaje sa ovog spiska.
          </>}
        >
          <ul className="storelist">
            {postings.map((posting) => (
              <li key={posting.documentNumber}>
                <span className="storelist__where">
                  Nalog {posting.documentNumber} <span className="muted">· {posting.operation}</span>
                  <span className="muted">
                    {' '}· {posting.from} → {posting.to} · {posting.articleCode} × {posting.quantity}
                    {posting.documentKey && ` · otpremnica ${posting.documentKey}`}
                  </span>
                </span>

                {posting.missingInErp ? (
                  <span className="flag flag--bad">nema ga u ERP-u</span>
                ) : (
                  <span className={posting.waitingDays > 1 ? 'flag flag--bad' : 'flag'}>
                    {posting.waitingDays === 0
                      ? 'danas'
                      : `${posting.waitingDays} ${dana(posting.waitingDays)}`}
                  </span>
                )}

                {posting.missingInErp && (
                  <span className="storelist__why muted">
                    Upisali smo ga, a u ERP-u ga nema. Pozovi magacin pre knjiženja.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      </>)}

      {view === 'razvoz' && <LocalDeliveryPanel code={code} />}

      {view === 'pregled' && (<>

      <p className="note">
        Ovde se ništa ne radi — samo se vidi šta se dešava sa tvojom prodajom i
        sa tvojim dopunama. Ono što traži tvoju ruku stoji na kartici
        „Šta me čeka".
      </p>

      <label className="field">
        Traži kupca, adresu ili otpremnicu
        <input
          id="pregled-trazi"
          value={trazi}
          onChange={(e) => setTrazi(e.target.value)}
          placeholder="npr. Petrović ili 004-1-4601"
        />
      </label>

      <Section
        {...veze('ceka', true)}
        title="Čeka isporuku"
        aside={String(waiting.length)}
      >
        {waiting.length === 0 ? (
          <p className="empty">Ništa ne čeka isporuku.</p>
        ) : (
          <ul className="storelist">
            {waiting.map((delivery) => (
              <li key={delivery.documentKey}>
                <span className="storelist__where">
                  {delivery.address}, {delivery.city}
                  <span className="muted"> · {delivery.customer}</span>
                </span>
                <span className={delivery.isOverdue ? 'flag flag--bad' : 'flag'}>
                  {delivery.waitingDays} {dana(delivery.waitingDays)}
                </span>
                {!delivery.canBePlanned && delivery.obstacle && (
                  <span className="storelist__why muted">{delivery.obstacle}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {onTour.length > 0 && (
        <Section
          {...veze('na-turi', false)}
          title="Na turi"
          aside={String(onTour.length)}
        >
          <ul className="storelist">
            {onTour.map((delivery) => (
              <li key={delivery.documentKey}>
                <span className="storelist__where">
                  {delivery.address}, {delivery.city}
                  <span className="muted"> · {delivery.customer}</span>
                </span>
                {/* Šta se tačno dešava — ne više „u vozilu" za sve (K24). */}
                <span className={
                  (delivery.tourNote ?? 'u vozilu').startsWith('u vozilu')
                    ? 'flag flag--ok'
                    : (delivery.tourNote ?? '').startsWith('vozač javio')
                      || (delivery.tourNote ?? '').startsWith('tura je završena')
                      ? 'flag flag--warn'
                      : 'flag'
                }>
                  {delivery.tourNote ?? 'u vozilu'}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {izjave.length > 0 && (
        <Section
          {...veze('izjave', false)}
          title="Kupac preuzeo ili odneo"
          aside={String(izjave.length)}
        >
          <ul className="storelist">
            {izjave
              .filter((z) => odgovara(z.documentKey, z.articleName))
              .map((z) => (
                <li key={z.id}>
                  <span className="storelist__where">
                    {z.articleName ?? z.articleCode} × {z.quantity}
                    <span className="muted"> · {z.documentKey} · {z.declaredBy}</span>
                  </span>
                  {z.drugaNoga ? (
                    <span className="storelist__why muted">
                      upisan je nalog {z.drugaNoga} u ERP-u — povlačenje ide preko operatera
                    </span>
                  ) : (
                    <button type="button" className="link" onClick={() => setPovlaci(z)}>
                      Povuci izjavu
                    </button>
                  )}
                </li>
              ))}
          </ul>
        </Section>
      )}

      {sklonjeno.length > 0 && (
        <Section
          {...veze('sklonjeno', false)}
          title="Sklonjeno iz prevoza"
          aside={String(sklonjeno.length)}
        >
          <ul className="storelist">
            {sklonjeno
              .filter((s) => odgovara(s.documentKey, s.reason))
              .map((s) => (
                <li key={s.documentKey}>
                  <span className="storelist__where">
                    {s.documentKey}
                    <span className="muted"> · {s.by ?? 'operater'} · {new Date(s.at).toLocaleDateString('sr-RS')}</span>
                  </span>
                  <span className="storelist__why muted">{s.reason}</span>
                </li>
              ))}
          </ul>
        </Section>
      )}

      {povlaci && (
        <Dialog
          title="Povući izjavu?"
          description={
            <>
              {povlaci.articleName ?? povlaci.articleCode} × {povlaci.quantity}, otpremnica{' '}
              <strong>{povlaci.documentKey}</strong>.
              <br /><br />
              Isporuka se <strong>vraća u prevoz</strong> — operater će je ponovo videti za
              razvoz. U ERP-u se ništa ne menja: uz ovu izjavu nalog nije upisan.
            </>
          }
          confirmLabel="Povuci izjavu"
          onCancel={() => setPovlaci(null)}
          onConfirm={() => void povuciIzjavu()}
        />
      )}

      {(delivered.prodato.length > 0 || delivered.primljeno.length > 0) && (
        <Section
          {...veze('isporuceno', false)}
          title="Isporučeno"
          aside="poslednje dve nedelje"
        >
          {delivered.prodato.length > 0 && (
            <>
              <p className="delivered__head">Tvoja prodaja koja je stigla kupcu</p>
              <ul className="rows">
                {delivered.prodato
                  .filter((row) => odgovara(row.label, row.city, row.documentKey))
                  .map((row) => (
                  <li key={row.stopId}>
                    <span className="row__name">
                      {row.label}{row.city ? `, ${row.city}` : ''}
                      {row.hasSignature && <span className="flag flag--ok"> potpisano</span>}
                    </span>
                    <span className="row__side">{isporuceno(row)}</span>
                    <span className="row__meta">
                      {row.items.join(', ')}
                      {row.documentKey ? ` · ${row.documentKey}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {delivered.primljeno.length > 0 && (
            <>
              {/* Drugo pitanje od gornjeg: „je li meni stigla dopuna". */}
              <p className="delivered__head">Dovezeno tebi</p>
              <ul className="rows">
                {delivered.primljeno.map((row) => (
                  <li key={row.stopId}>
                    <span className="row__name">{row.items.join(', ')}</span>
                    <span className="row__side">{isporuceno(row)}</span>
                    <span className="row__meta">{row.tourName}{row.driver ? ` · ${row.driver}` : ''}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Section>
      )}

      <Section
        {...veze('pravci', false)}
        title="Pravci razvoza"
        note={<>
          Kad koji pravac ide i kroz koja mesta prolazi. Raspored vodi magacin —
          ovde se ne menja. Izaberi pravac da ga vidiš na mapi.
        </>}
      >
        {corridors.filter((c) => c.isActive).length === 0 ? (
          <p className="empty">Nema upisanih pravaca.</p>
        ) : (
          <ul className="destinations">
            {corridors.filter((c) => c.isActive).map((corridor) => (
              <li key={corridor.id}>
                <button
                  type="button"
                  className={`destination ${openCorridorId === corridor.id ? 'destination--selected' : ''}`}
                  onClick={() => {
                    setOpenCorridorId(openCorridorId === corridor.id ? null : corridor.id);
                    setHighlightPlaceId(null);
                  }}
                >
                  <span className="destination__name">{corridor.name}</span>
                  <span className="destination__meta">
                    {corridor.places.map((place) => place.placeName).join(' → ')}
                  </span>
                  <span className="destination__waiting">{corridor.days || 'bez rasporeda'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {openCorridor && (
          <CorridorMap
            places={openCorridor.places}
            selectedId={highlightPlaceId}
            onSelect={setHighlightPlaceId}
            onMove={() => undefined}
            readOnly
          />
        )}
      </Section>

      </>)}
    </div>
  );
}
